import { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import { CLASS_STAGES } from '../../utils';

/**
 * Everything the roster can be narrowed by.
 *
 * One object rather than seven pieces of state threaded through as props: the
 * page resets them together, counts them together, and will send them to the
 * server together the day this list outgrows filtering in the browser.
 */
export interface ClassFilters {
  search: string;
  /**
   * A session name, or `''` for "whichever the server returned".
   *
   * `GET /institute/classes` answers for the current year only, so this narrows
   * nothing today — it is a real filter over `academicYear.name` so that it
   * starts working the moment the endpoint carries a second session, rather
   * than a control that has to be rewritten then.
   */
  academicYear: string;
  /** A class uuid — "Grade" here is one class, not a stage. */
  grade: string;
  /** A teacher uuid. */
  teacher: string;
  stage: string;
  medium: string;
  /**
   * Teaching order, either way up. An ordering rather than a narrowing, so
   * `isNarrowed` ignores it; Clear still puts it back to lowest first.
   */
  sort: ClassSortOrder;
}

export type ClassSortOrder = 'LOWEST_FIRST' | 'HIGHEST_FIRST';

/** Exported so the page can check a sort read from the address against it. */
export const SORT_OPTIONS: Option[] = [
  { value: 'LOWEST_FIRST', label: 'Lowest class first' },
  { value: 'HIGHEST_FIRST', label: 'Highest class first' },
];

export const EMPTY_CLASS_FILTERS: ClassFilters = {
  search: '',
  academicYear: '',
  grade: 'ALL',
  teacher: 'ALL',
  stage: 'ALL',
  medium: 'ALL',
  sort: 'LOWEST_FIRST',
};

/**
 * The two behind "More filters" — counted so the button can say how many are on.
 * No board: every class follows the school's own, so there is nothing to choose.
 */
const ADVANCED: (keyof ClassFilters)[] = ['stage', 'medium'];

/**
 * Whether anything at all is narrowing the roster.
 *
 * The session counts once it has been chosen deliberately: an empty list under
 * a closed year means "nothing that year", not "nothing ever". Left unset it is
 * simply the year the server answered for, which narrows nothing.
 */
export function isNarrowed(filters: ClassFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.academicYear !== '' ||
    filters.grade !== 'ALL' ||
    filters.teacher !== 'ALL' ||
    ADVANCED.some((key) => filters[key] !== 'ALL')
  );
}

const ALL = { value: 'ALL', label: 'All' };

const STAGE_OPTIONS: Option[] = [
  { value: 'ALL', label: 'All stages' },
  ...CLASS_STAGES.map((stage) => ({ value: stage, label: stage })),
];

export interface ClassesFilterBarProps {
  filters: ClassFilters;
  onChange: (patch: Partial<ClassFilters>) => void;
  onClear: () => void;
  /** The sessions the roster mentions. Today the server answers for one. */
  yearOptions: Option[];
  /** Every class on the roster, for the grade dropdown. */
  gradeOptions: Option[];
  /** Every teacher holding a section, for the class-teacher dropdown. */
  teacherOptions: Option[];
  /** The school's mediums, for the medium dropdown — see `schoolMediumOptions`. */
  mediumOptions: Option[];
  /** How many classes are left after all of this. */
  resultCount: number;
  /** Dropdowns stay inert until there is a roster to narrow. */
  isLoading?: boolean;
}

export function ClassesFilterBar({
  filters,
  onChange,
  onClear,
  yearOptions,
  gradeOptions,
  teacherOptions,
  mediumOptions,
  resultCount: _resultCount,
  isLoading = false,
}: ClassesFilterBarProps) {
  const advancedCount = ADVANCED.filter((key) => filters[key] !== 'ALL').length;
  // Opens by itself when something behind it is already in force, so a filter
  // narrowing the list is never hidden from the person reading it.
  const [showAdvanced, setShowAdvanced] = useState(advancedCount > 0);

  const selectClass =
    '!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors';

  return (
    <section
      aria-label="Filter classes"
      className="rounded-2xl p-2.5 bg-slate-50 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs flex flex-col gap-2.5"
    >
      <div className="flex flex-wrap items-center gap-2">
       

        <div className="w-full sm:w-auto sm:flex-1 min-w-0 sm:min-w-[200px]">
          <Input
            label="Search classes"
            hideLabel
            type="search"
            placeholder="Search by class, section or subject…"
            leftIcon={<Search className="h-3.5 w-3.5 text-slate-400" aria-hidden />}
            value={filters.search}
            onChange={(event) => onChange({ search: event.target.value })}
            className="!h-9 !py-1 !pl-9 !pr-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
          />
        </div>

        <div className="w-[calc(50%-0.25rem)] sm:w-[165px] shrink-0">
          <Select
            label="Sort classes"
            hideLabel
            disabled={isLoading}
            options={SORT_OPTIONS}
            className={selectClass}
            value={filters.sort}
            onChange={(event) => onChange({ sort: event.target.value as ClassSortOrder })}
          />
        </div>

        <div className="w-[calc(50%-0.25rem)] sm:w-[145px] shrink-0">
          <Select
            label="Academic year"
            hideLabel
            disabled={isLoading || yearOptions.length === 0}
            options={yearOptions}
            className={selectClass}
            value={filters.academicYear || yearOptions[0]?.value || ''}
            onChange={(event) => onChange({ academicYear: event.target.value })}
          />
        </div>

        <div className="w-[calc(50%-0.25rem)] sm:w-[140px] shrink-0">
          <Select
            label="Grade"
            hideLabel
            disabled={isLoading}
            options={[{ value: 'ALL', label: 'Classes' }, ...gradeOptions]}
            className={selectClass}
            value={filters.grade}
            onChange={(event) => onChange({ grade: event.target.value })}
          />
        </div>

        <div className="w-[calc(50%-0.25rem)] sm:w-[155px] shrink-0">
          <Select
            label="Class teacher"
            hideLabel
            disabled={isLoading}
            options={[{ value: 'ALL', label: 'All teachers' }, ...teacherOptions]}
            className={selectClass}
            value={filters.teacher}
            onChange={(event) => onChange({ teacher: event.target.value })}
          />
        </div>

        <Button
          variant="secondary"
          size="sm"
          aria-expanded={showAdvanced}
          className="!h-9 !py-1 !px-3 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors"
          leftIcon={<SlidersHorizontal className="h-3.5 w-3.5 text-indigo-500" />}
          onClick={() => setShowAdvanced((open) => !open)}
        >
          <span>More filters</span>
          {advancedCount > 0 && (
            <span className="ml-1 grid h-4.5 min-w-4.5 place-items-center rounded-full bg-indigo-100 dark:bg-indigo-900/60 px-1 text-[11px] font-black text-indigo-600 dark:text-indigo-300">
              {advancedCount}
            </span>
          )}
        </Button>

        {isNarrowed(filters) && (
          <Button
            variant="secondary"
            size="sm"
            className="!h-9 !py-1 !px-3 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-rose-600 hover:border-rose-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors"
            leftIcon={<X className="h-3.5 w-3.5 text-rose-500" />}
            onClick={onClear}
          >
            Clear
          </Button>
        )}
      </div>

      {showAdvanced && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-200/80 dark:border-slate-800 pt-2.5">
          <div className="w-[calc(50%-0.25rem)] sm:w-[145px] shrink-0">
            <Select
              label="Stage"
              hideLabel
              options={STAGE_OPTIONS}
              className={selectClass}
              value={filters.stage}
              onChange={(event) => onChange({ stage: event.target.value })}
            />
          </div>

          {/* Nothing to narrow by when every class is taught in the same medium. */}
          {mediumOptions.length > 1 && (
            <div className="w-[calc(50%-0.25rem)] sm:w-[145px] shrink-0">
              <Select
                label="Medium"
                hideLabel
                options={[{ ...ALL, label: 'All mediums' }, ...mediumOptions]}
                className={selectClass}
                value={filters.medium}
                onChange={(event) => onChange({ medium: event.target.value })}
              />
            </div>
          )}
        </div>
      )}

      {/* <div className="flex items-center justify-between gap-3 px-1 pt-0.5">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {isLoading ? (
            'Loading the roster…'
          ) : (
            <>
              <span className="font-bold text-slate-700 dark:text-slate-200">{resultCount}</span>{' '}
              {resultCount === 1 ? 'class' : 'classes'} match this view
            </>
          )}
        </p>
      </div> */}
    </section>
  );
}
